// KontaktRollenSection.tsx — Einstellungen → Kontaktrollen (User 27.09.2026:
// „Bereiche und dazugehörige Rollen müssen einstellbar sein").
//
// Pflegt den Rollenkatalog unter Status „Sonstige" in der Kontaktanlage je
// Mandant: Bereiche anlegen/umbenennen/sortieren/deaktivieren, je Bereich
// Rollen anlegen/umbenennen/sortieren/deaktivieren samt Typ (Person,
// Unternehmen, beides), Gegenüber-Frage und Folgefeldern.
//
// Regeln, die die Maske sichtbar macht:
//  - Umbenennen ändert nur die ANZEIGE. Gespeichert wird eine Rolle unter dem
//    Namen, den sie beim Anlegen hatte — bestehende Kontakte behalten sie.
//  - Entfernen heißt deaktivieren: nicht mehr wählbar, an bestehenden
//    Kontakten weiter sichtbar, jederzeit wieder einschaltbar.
// Backend: apps/api/src/kontakt-rollen/ (Schreiben nur admin).

import * as React from "react";
import { MessageBar, MessageBarBody, Spinner, makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../components/risikoanalyseStep/tokens";
import { SectionCard, useFormPrimitiveStyles } from "../../components/forms/formPrimitives";
import {
  bereichAendern,
  bereichAnlegen,
  bereicheSortieren,
  kontaktRollenLaden,
  rolleAendern,
  rolleAnlegen,
  rollenSortieren,
  type KontaktRolleEintrag,
  type KontaktRollenBereichEintrag,
  type RollenEingabe,
} from "../../services/kontaktRollenService";
import { kontaktRollenNeuLaden } from "../../services/useKontaktRollen";
import type { RollenFolge, RollenTyp } from "../../data/kontaktRollen";
import type { BezugsArt } from "../../data/statusAuswahlKatalog";

const TYP_OPTIONEN: Array<{ value: RollenTyp; label: string }> = [
  { value: "beide", label: "Person oder Unternehmen" },
  { value: "person", label: "nur Person (Privat)" },
  { value: "firma", label: "nur Unternehmen" },
];

const BEZUG_OPTIONEN: Array<{ value: "" | BezugsArt; label: string }> = [
  { value: "", label: "keine Rückfrage" },
  { value: "kontakt", label: "„für wen?“ — Person oder Unternehmen" },
  { value: "firma", label: "„für wen?“ — ein Unternehmen" },
  { value: "person", label: "„für wen?“ — eine Person" },
];

const FOLGE_OPTIONEN: Array<{ value: "" | RollenFolge; label: string }> = [
  { value: "", label: "keine" },
  { value: "unternehmen", label: "Unternehmen + Verantwortlichkeit" },
  { value: "verein", label: "Verein + Verantwortlichkeit" },
  { value: "person", label: "Person mit Beziehung (Haushalt)" },
];

const useStyles = makeStyles({
  wurzel: { display: "flex", flexDirection: "column", rowGap: "16px", fontFamily: REFINED.fontUI },
  intro: { display: "flex", flexDirection: "column", rowGap: "6px", color: REFINED.ink2, fontSize: "0.875rem", lineHeight: 1.5 },
  kopfzeile: { display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", columnGap: "12px", rowGap: "8px" },
  aktionen: { display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "6px", rowGap: "6px" },
  knopf: {
    appearance: "none",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    ...shorthands.padding("6px", "10px"),
    ":hover": { backgroundColor: REFINED.aub50, ...shorthands.borderColor(REFINED.aub200) },
    ":disabled": { opacity: 0.45, cursor: "default" },
  },
  primaer: {
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    ...shorthands.borderColor(REFINED.aub),
    ":hover": { backgroundColor: REFINED.aub700, ...shorthands.borderColor(REFINED.aub700) },
  },
  pfeil: { minWidth: "30px", ...shorthands.padding("6px", "6px") },
  zeilen: { display: "flex", flexDirection: "column", rowGap: "8px" },
  rolle: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1.1fr) auto",
    columnGap: "10px",
    rowGap: "8px",
    alignItems: "end",
    ...shorthands.padding("10px", "12px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    "@media (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  inaktiv: { backgroundColor: REFINED.fieldBg, opacity: 0.7 },
  feld: { display: "flex", flexDirection: "column", rowGap: "5px", minWidth: 0 },
  feldLabel: { fontSize: "0.72rem", fontWeight: 600, color: REFINED.ink2, textTransform: "uppercase", letterSpacing: "0.04em" },
  name: { fontSize: "0.9rem", fontWeight: 600, color: REFINED.ink, overflowWrap: "anywhere" },
  hinweis: { fontSize: "0.75rem", color: REFINED.ink3 },
  abzeichen: {
    display: "inline-block",
    marginLeft: "6px",
    ...shorthands.padding("1px", "7px"),
    ...shorthands.borderRadius("999px"),
    backgroundColor: REFINED.goldBg,
    color: REFINED.goldInk,
    fontSize: "0.7rem",
    fontWeight: 600,
  },
  check: { display: "flex", alignItems: "center", columnGap: "6px", fontSize: "0.8rem", color: REFINED.ink2 },
  neu: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    columnGap: "10px",
    rowGap: "8px",
    ...shorthands.padding("12px"),
    ...shorthands.border("1px", "dashed", REFINED.aub200),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.aub50,
  },
  neuFeld: { display: "flex", flexDirection: "column", rowGap: "5px", minWidth: "200px", flexGrow: 1 },
  leer: { color: REFINED.ink3, fontSize: "0.85rem" },
});

/** Inline-Umbenennen: zeigt den Namen, auf Knopfdruck ein Eingabefeld. */
function Umbenennen({
  wert,
  onSpeichern,
  busy,
  label,
}: {
  wert: string;
  onSpeichern: (neu: string) => Promise<boolean>;
  busy: boolean;
  label: string;
}) {
  const s = useStyles();
  const fp = useFormPrimitiveStyles();
  const [offen, setOffen] = React.useState(false);
  const [text, setText] = React.useState(wert);
  if (!offen) {
    return (
      <button type="button" className={s.knopf} disabled={busy} onClick={() => { setText(wert); setOffen(true); }}>
        Umbenennen
      </button>
    );
  }
  const speichern = async () => {
    if (!text.trim() || text.trim() === wert) return setOffen(false);
    if (await onSpeichern(text.trim())) setOffen(false);
  };
  return (
    <span className={s.aktionen}>
      <input
        className={fp.input}
        style={{ minWidth: 220 }}
        aria-label={label}
        value={text}
        autoFocus
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void speichern();
          if (e.key === "Escape") setOffen(false);
        }}
      />
      <button type="button" className={mergeClasses(s.knopf, s.primaer)} disabled={busy} onClick={() => void speichern()}>
        Speichern
      </button>
      <button type="button" className={s.knopf} disabled={busy} onClick={() => setOffen(false)}>
        Abbrechen
      </button>
    </span>
  );
}

function Auswahl<T extends string>({
  label,
  value,
  optionen,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  optionen: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  const s = useStyles();
  const fp = useFormPrimitiveStyles();
  return (
    <label className={s.feld}>
      <span className={s.feldLabel}>{label}</span>
      <select
        className={mergeClasses(fp.input, fp.selectControl)}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {optionen.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Verschiebt `id` in `ids` um `richtung` Plätze — neue Reihenfolge oder null. */
export function verschoben(ids: number[], id: number, richtung: -1 | 1): number[] | null {
  const i = ids.indexOf(id);
  const j = i + richtung;
  if (i < 0 || j < 0 || j >= ids.length) return null;
  const neu = [...ids];
  [neu[i], neu[j]] = [neu[j], neu[i]];
  return neu;
}

function RollenZeile({
  rolle,
  ersteZeile,
  letzteZeile,
  busy,
  run,
  onVerschieben,
}: {
  rolle: KontaktRolleEintrag;
  ersteZeile: boolean;
  letzteZeile: boolean;
  busy: boolean;
  run: (fn: () => Promise<unknown>) => Promise<boolean>;
  onVerschieben: (richtung: -1 | 1) => void;
}) {
  const s = useStyles();
  const aendern = (patch: RollenEingabe) => run(() => rolleAendern(rolle.id, patch));
  return (
    <div className={mergeClasses(s.rolle, !rolle.aktiv && s.inaktiv)}>
      <div className={s.feld}>
        <span className={s.feldLabel}>Rolle</span>
        <span className={s.name}>
          {rolle.label}
          {!rolle.aktiv && <span className={s.abzeichen}>deaktiviert</span>}
        </span>
        {rolle.value !== rolle.label && <span className={s.hinweis}>gespeichert als „{rolle.value}“</span>}
        <label className={s.check}>
          <input
            type="checkbox"
            checked={rolle.nurCoIntern}
            disabled={busy}
            onChange={(e) => void aendern({ nurCoIntern: e.target.checked })}
          />
          nur mit Recht „CO intern“
        </label>
      </div>
      <Auswahl<RollenTyp>
        label="Passt zu"
        value={rolle.typ}
        optionen={TYP_OPTIONEN}
        disabled={busy}
        onChange={(v) => void aendern({ typ: v })}
      />
      <Auswahl<"" | BezugsArt>
        label="Gegenüber"
        value={rolle.folge ? "" : rolle.bezug ?? ""}
        optionen={BEZUG_OPTIONEN}
        disabled={busy || !!rolle.folge}
        onChange={(v) => void aendern({ bezug: v || null })}
      />
      <Auswahl<"" | RollenFolge>
        label="Folgefelder"
        value={rolle.folge ?? ""}
        optionen={FOLGE_OPTIONEN}
        disabled={busy}
        onChange={(v) => void aendern({ folge: v || null })}
      />
      <div className={s.aktionen}>
        <button type="button" className={mergeClasses(s.knopf, s.pfeil)} title="nach oben" aria-label="nach oben" disabled={busy || ersteZeile} onClick={() => onVerschieben(-1)}>
          ↑
        </button>
        <button type="button" className={mergeClasses(s.knopf, s.pfeil)} title="nach unten" aria-label="nach unten" disabled={busy || letzteZeile} onClick={() => onVerschieben(1)}>
          ↓
        </button>
        <Umbenennen wert={rolle.label} busy={busy} label="Neue Bezeichnung der Rolle" onSpeichern={(label) => aendern({ label })} />
        <button type="button" className={s.knopf} disabled={busy} onClick={() => void aendern({ aktiv: !rolle.aktiv })}>
          {rolle.aktiv ? "Deaktivieren" : "Aktivieren"}
        </button>
      </div>
    </div>
  );
}

function NeueRolle({
  busy,
  onAnlegen,
}: {
  busy: boolean;
  onAnlegen: (e: RollenEingabe & { label: string }) => Promise<boolean>;
}) {
  const s = useStyles();
  const fp = useFormPrimitiveStyles();
  const [offen, setOffen] = React.useState(false);
  const [label, setLabel] = React.useState("");
  const [typ, setTyp] = React.useState<RollenTyp>("beide");
  const [bezug, setBezug] = React.useState<"" | BezugsArt>("");
  const [folge, setFolge] = React.useState<"" | RollenFolge>("");
  if (!offen) {
    return (
      <button type="button" className={s.knopf} style={{ alignSelf: "flex-start" }} disabled={busy} onClick={() => setOffen(true)}>
        + Rolle
      </button>
    );
  }
  const anlegen = async () => {
    if (!label.trim()) return;
    const ok = await onAnlegen({ label: label.trim(), typ, bezug: folge ? null : bezug || null, folge: folge || null });
    if (ok) {
      setLabel("");
      setTyp("beide");
      setBezug("");
      setFolge("");
      setOffen(false);
    }
  };
  return (
    <div className={s.neu}>
      <label className={s.neuFeld}>
        <span className={s.feldLabel}>Bezeichnung der neuen Rolle</span>
        <input
          className={fp.input}
          value={label}
          autoFocus
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void anlegen();
          }}
        />
      </label>
      <Auswahl<RollenTyp> label="Passt zu" value={typ} optionen={TYP_OPTIONEN} onChange={setTyp} />
      <Auswahl<"" | BezugsArt> label="Gegenüber" value={folge ? "" : bezug} optionen={BEZUG_OPTIONEN} disabled={!!folge} onChange={setBezug} />
      <Auswahl<"" | RollenFolge> label="Folgefelder" value={folge} optionen={FOLGE_OPTIONEN} onChange={setFolge} />
      <div className={s.aktionen}>
        <button type="button" className={mergeClasses(s.knopf, s.primaer)} disabled={busy || !label.trim()} onClick={() => void anlegen()}>
          Anlegen
        </button>
        <button type="button" className={s.knopf} disabled={busy} onClick={() => setOffen(false)}>
          Abbrechen
        </button>
      </div>
      <span className={s.hinweis} style={{ flexBasis: "100%" }}>
        Unter dieser Bezeichnung wird die Rolle gespeichert. Späteres Umbenennen ändert nur die Anzeige.
      </span>
    </div>
  );
}

export function KontaktRollenSection() {
  const s = useStyles();
  const fp = useFormPrimitiveStyles();
  const [bereiche, setBereiche] = React.useState<KontaktRollenBereichEintrag[]>([]);
  const [laedt, setLaedt] = React.useState(true);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [neuerBereichOffen, setNeuerBereichOffen] = React.useState(false);
  const [neuerBereich, setNeuerBereich] = React.useState("");

  const neuLaden = React.useCallback(async () => {
    try {
      const k = await kontaktRollenLaden();
      setBereiche(Array.isArray(k?.bereiche) ? k.bereiche : []);
    } catch (e: any) {
      setFehler(
        e?.status === 404
          ? "Der Server kennt die Kontaktrollen-Einstellungen noch nicht (älterer Stand). Die Anlage nutzt den eingebauten Katalog."
          : String(e?.message ?? e),
      );
    } finally {
      setLaedt(false);
    }
  }, []);

  React.useEffect(() => {
    void neuLaden();
  }, [neuLaden]);

  const run = React.useCallback(
    async (fn: () => Promise<unknown>): Promise<boolean> => {
      setBusy(true);
      setFehler(null);
      try {
        await fn();
        return true;
      } catch (e: any) {
        setFehler(String(e?.message ?? e));
        return false;
      } finally {
        // Auch nach einem Fehler neu laden — der Stand könnte sich geändert haben.
        kontaktRollenNeuLaden();
        await neuLaden();
        setBusy(false);
      }
    },
    [neuLaden],
  );

  const bereichAnlegenAusfuehren = async () => {
    const titel = neuerBereich.trim();
    if (!titel) return;
    if (await run(() => bereichAnlegen(titel))) {
      setNeuerBereich("");
      setNeuerBereichOffen(false);
    }
  };

  const bereichIds = bereiche.map((b) => b.id);

  return (
    <div className={s.wurzel}>
      <SectionCard
        heading="Kontaktrollen"
        actions={
          <button type="button" className={mergeClasses(s.knopf, s.primaer)} disabled={busy || laedt} onClick={() => setNeuerBereichOffen(true)}>
            + Bereich
          </button>
        }
      >
        <div className={s.intro}>
          <span>
            Die Bereiche und Rollen, die in der Kontaktanlage unter Status „Sonstige“ zur Wahl stehen. „Passt zu“
            entscheidet, ob die Anlage daraus Unternehmen oder Privat ableitet.
          </span>
          <span>
            Umbenennen ändert nur die Anzeige — Kontakte, die die Rolle schon tragen, behalten sie. Entfernen heißt
            deaktivieren: nicht mehr wählbar, an bestehenden Kontakten weiter sichtbar.
          </span>
        </div>
        {neuerBereichOffen && (
          <div className={s.neu} style={{ marginTop: 12 }}>
            <label className={s.neuFeld}>
              <span className={s.feldLabel}>Titel des neuen Bereichs</span>
              <input
                className={fp.input}
                value={neuerBereich}
                autoFocus
                onChange={(e) => setNeuerBereich(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void bereichAnlegenAusfuehren();
                  if (e.key === "Escape") setNeuerBereichOffen(false);
                }}
              />
            </label>
            <div className={s.aktionen}>
              <button type="button" className={mergeClasses(s.knopf, s.primaer)} disabled={busy || !neuerBereich.trim()} onClick={() => void bereichAnlegenAusfuehren()}>
                Anlegen
              </button>
              <button type="button" className={s.knopf} disabled={busy} onClick={() => setNeuerBereichOffen(false)}>
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </SectionCard>

      {fehler && (
        <MessageBar intent="error">
          <MessageBarBody>{fehler}</MessageBarBody>
        </MessageBar>
      )}

      {laedt ? (
        <Spinner label="Lade Kontaktrollen…" />
      ) : bereiche.length === 0 && !fehler ? (
        <span className={s.leer}>Noch keine Bereiche.</span>
      ) : (
        bereiche.map((b, bi) => {
          const aktiveRollen = b.rollen.filter((r) => r.aktiv).length;
          const rollenIds = b.rollen.map((r) => r.id);
          return (
            <SectionCard
              key={b.id}
              collapsible
              defaultCollapsed
              title={`${aktiveRollen} von ${b.rollen.length} Rollen aktiv${b.aktiv ? "" : " · Bereich deaktiviert"}`}
              heading={b.titel}
              className={b.aktiv ? undefined : s.inaktiv}
              actions={
                <div className={s.aktionen}>
                  <button
                    type="button"
                    className={mergeClasses(s.knopf, s.pfeil)}
                    title="Bereich nach oben"
                    aria-label="Bereich nach oben"
                    disabled={busy || bi === 0}
                    onClick={() => {
                      const ids = verschoben(bereichIds, b.id, -1);
                      if (ids) void run(() => bereicheSortieren(ids));
                    }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={mergeClasses(s.knopf, s.pfeil)}
                    title="Bereich nach unten"
                    aria-label="Bereich nach unten"
                    disabled={busy || bi === bereiche.length - 1}
                    onClick={() => {
                      const ids = verschoben(bereichIds, b.id, 1);
                      if (ids) void run(() => bereicheSortieren(ids));
                    }}
                  >
                    ↓
                  </button>
                  <Umbenennen
                    wert={b.titel}
                    busy={busy}
                    label="Neuer Titel des Bereichs"
                    onSpeichern={(titel) => run(() => bereichAendern(b.id, { titel }))}
                  />
                  <button type="button" className={s.knopf} disabled={busy} onClick={() => void run(() => bereichAendern(b.id, { aktiv: !b.aktiv }))}>
                    {b.aktiv ? "Deaktivieren" : "Aktivieren"}
                  </button>
                </div>
              }
            >
              <div className={s.zeilen}>
                {b.rollen.length === 0 && <span className={s.leer}>Noch keine Rollen in diesem Bereich.</span>}
                {b.rollen.map((r, ri) => (
                  <RollenZeile
                    key={r.id}
                    rolle={r}
                    ersteZeile={ri === 0}
                    letzteZeile={ri === b.rollen.length - 1}
                    busy={busy}
                    run={run}
                    onVerschieben={(richtung) => {
                      const ids = verschoben(rollenIds, r.id, richtung);
                      if (ids) void run(() => rollenSortieren(b.id, ids));
                    }}
                  />
                ))}
                <NeueRolle busy={busy} onAnlegen={(e) => run(() => rolleAnlegen(b.id, e))} />
              </div>
            </SectionCard>
          );
        })
      )}
    </div>
  );
}
